import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/authSlice';

interface TopBarProps {
  onMenuClick: () => void;
}

const PATH_TITLES: Record<string, string> = {
  '/': 'My Forms',
  '/my/drafts': 'My Drafts',
  '/my/published': 'My Published Blogs',
  '/profile': 'Profile Settings',
  '/admin/sprint-actions': 'Sprint Actions',
  '/admin/sprints': 'All Sprints',
  '/admin/forms': 'All Submissions',
  '/admin/user': 'User Management',
  '/admin/admins': 'Admin Management',
  '/admin/invites': 'Invitations',
  '/admin/published-blogs': 'Published Blogs',
  '/admin/calendar': 'Content Calendar',
  '/admin/backlinks-hub': 'Backlinks Hub',
};

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Derive page title from path
  let title = 'Dashboard';
  for (const [path, t] of Object.entries(PATH_TITLES)) {
    if (location.pathname === path || (path !== '/' && location.pathname.startsWith(path))) {
      title = t;
      break;
    }
  }
  if (location.pathname.startsWith('/admin/sprints/') && location.pathname !== '/admin/sprints') {
    title = 'Sprint Details';
  } else if (location.pathname.startsWith('/form/')) {
    title = 'Blog Form';
  } else if (location.pathname.startsWith('/admin/blog/')) {
    title = 'Blog Preview';
  }

  const initials = user?.name
    ? user.name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('')
    : 'U';

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-trident-gray-100">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onMenuClick}
            className="lg:hidden w-9 h-9 rounded-lg text-trident-gray-600 hover:text-trident-orange hover:bg-trident-orange-light flex items-center justify-center transition"
            aria-label="Open menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-xl text-trident-gray-900 tracking-tight truncate">
              {title}
            </h1>
            <div className="hidden sm:block text-xs text-trident-gray-500 truncate">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </div>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 p-1 rounded-xl hover:bg-trident-gray-50 transition"
          >
            <div className="w-9 h-9 rounded-xl bg-trident-gradient flex items-center justify-center text-white font-display font-bold text-xs shadow-trident">
              {initials}
            </div>
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-card-hover border border-trident-gray-100 py-2 animate-slide-up overflow-hidden z-40">
              <div className="px-4 py-3 border-b border-trident-gray-100">
                <div className="font-display font-bold text-trident-gray-900 truncate">{user?.name || 'Account'}</div>
                <div className="text-xs text-trident-gray-500 truncate">{user?.email}</div>
              </div>
              <Link
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-trident-gray-700 hover:bg-trident-orange-light hover:text-trident-orange transition font-semibold"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                Profile Settings
              </Link>
              <div className="border-t border-trident-gray-100 my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition font-semibold"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
